import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { BackgroundJobDto } from "@/api/dtos";
import type { BackgroundJob } from "./_models/BackgroundJob";
import { toBackgroundJobFromBackgroundJobDto } from "./_transformers/toBackgroundJobFromBackgroundJobDto";
import { BackgroundJobTags } from "./BackgroundJobTags";

/** One background job (GET /background-jobs/{backgroundJobId}); a missing job is the backend's 404 as the query's error. Poll while it's active. */
export const getBackgroundJobApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBackgroundJob: builder.query<BackgroundJob, string>({
      queryFn: async (backgroundJobId) => {
        try {
          const res = await axios.get<BackgroundJobDto>(
            `${backendBase()}/background-jobs/${encodeURIComponent(backgroundJobId)}`,
            { headers: { Accept: "application/json" } },
          );
          const job = toBackgroundJobFromBackgroundJobDto(res.data);
          return { data: job };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (_result, _error, backgroundJobId) => [{ type: BackgroundJobTags.BackgroundJob, id: backgroundJobId }],
    }),
  }),
});

export const getBackgroundJob = getBackgroundJobApi.endpoints.getBackgroundJob;
